/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Rectangle } from 'pixi.js-legacy';
import { Ticket } from '@touchplan/common';
import * as _ from 'lodash-es';
import { TimelineTicketList } from 'src/app/common/fancy-firebase-ticket/lists/TimelineTicketList';
import { TicketMeta } from 'src/app/app-data/models/TicketMeta';
import { Observable, EMPTY, of, MonoTypeOperatorFunction } from 'rxjs';
import { FancyAction } from '../fancy-firebase/base/FancyAction';
import { FancyActionTypes } from '../fancy-firebase/base/FancyActionTypes';
import { filter, scan, pluck, shareReplay, withLatestFrom, switchMap } from 'rxjs/operators';
import { ICustomFieldTicket } from 'src/app/app-data/models/CustomFieldTicketValue';
import { ITicketAttachments } from '../../app-data/models/Attachment';

/** A standard set of invalidation checks to see if the active line has moved in a way to conflicts with the cached data
 * Returns true if invalid
 */
export function activeLineInvalidationCheck(
	currentActiveLineX: number,
	oldActiveLineX: number,
	itemRect: Rectangle
) {
	const noOldLine = oldActiveLineX === undefined || oldActiveLineX === null;
	const noNewLine = currentActiveLineX === undefined || currentActiveLineX === null;

	// - active line starts right of ticket selection, active line is left or overlapping selection, invalid
	// - active line did not exist, active line does exist, if left of tickets, invalid
	const crossedRightToLeft =
		((noOldLine && !noNewLine) || oldActiveLineX >= itemRect.right) && currentActiveLineX < itemRect.right;

	// - active line starts left of ticket selection, active line is right or overlapping selection, invalid
	// - active line did exist, active line does not exist, if was right of tickets, invalid
	const crossedLeftToRight =
		((!noOldLine && noNewLine) || oldActiveLineX < itemRect.left) && currentActiveLineX >= itemRect.left;

	// - active line starts overlapping ticket selection, any active line update is invalid
	const startsOverlapping = !noOldLine && oldActiveLineX >= itemRect.left && oldActiveLineX < itemRect.right;

	return startsOverlapping || crossedRightToLeft || crossedLeftToRight;
}

//eventually this will do something more... efficient, but for now, deep copy and leave this function as a placeholder of sorts
export function serializeTicket(ticket: Ticket) {
	return _.cloneDeep(ticket);
}

export function metaizeTicket(
	ticket: Ticket,
	timelineTicketId: string | null,
	customFieldTicketValues?: ICustomFieldTicket,
	ticketAttachmentsData?: ITicketAttachments
) {
	return new TicketMeta(ticket, timelineTicketId, customFieldTicketValues, ticketAttachmentsData);
}

//used by undo, consider moving elsewhere
// returns the combinedId if the ticket is in the timeline, null, if it's not
export function getTimelineIdForTicket(ticket: Ticket, timelineTickets: TimelineTicketList, planId: string) {
	let combinedId: string;
	if (ticket.isTimelineTicket) {
		combinedId = ticket.$id;
	} else {
		combinedId = timelineTickets.makeCombinedId(ticket.$id, planId);
	}
	return timelineTickets._internalList.has(combinedId) ? combinedId : null;
}

/**
 * emits a list of ticket actions every time any delete action fires, with an event containing a list of accumulated delete actions
 * This list is reset whenever a child_removed emits and does not include the child_removed action in the output.
 * Share (replay) the observable so there's not a bunch of things doing this, it's a reasonably expensive operation
 */
export function monitorPreviousDeletion$<Action extends FancyAction>(
	events$: Observable<Action>,
	isRemoveCb: (action: Action) => boolean
): Observable<Action[]> {
	return events$.pipe(
		filter((action) => isRemoveCb(action)),
		scan(
			(acc, action) => {
				if (acc.clearNext) {
					acc.list = [];
					acc.clearNext = false;
				}

				if (action.type === FancyActionTypes.childRemoved) {
					acc.clearNext = true;
				} else {
					acc.list.push(action);
				} //else-ing this will result in one extra emission with no change, but... w/e

				return acc;
			},
			{ list: [], clearNext: false }
		),
		pluck('list'),
		shareReplay()
	);
}

/**
 * When paired with another observable detecting previous emissions (see [[monitorPreviousDeletion]]). This can detect with _reasonable_
 * confidence the difference between an externally made (by another user) and a deletion done by this client
 */
// tslint:disable-next-line: max-line-length
export function monitorExternalDeletion<Action extends FancyAction>(
	previousDeletion: Observable<Action[]>
): MonoTypeOperatorFunction<Action> {
	return function monitorExternalDeletionImplementation(srcObs) {
		return srcObs.pipe(
			filter((action) => action.type === FancyActionTypes.childRemoved),
			withLatestFrom(previousDeletion),
			switchMap((combined) => {
				let count = 0;
				combined[1].forEach((bufferedAction) => {
					if (bufferedAction.key === combined[0].key) {
						count++;
					}
				});
				if (count === 0) {
					return of(combined[0]);
				}
				return EMPTY;
			})
		);
	};
}
